import { Button, Typography, useTheme } from '@material-ui/core'
import dayjs from 'dayjs'
import { useState } from 'react'
import { SchoolPersonFragment } from '../../graphql/autogenerate/operations'
import {
  useDeleteSchoolPersonInvitationMutation,
  useSendSchoolPersonInvitationEmailMutation,
} from '../../graphql/autogenerate/react-query'
import { useHandleReactQueryMutation } from '../../hooks'
import { useButtonStyles } from '../../styles'
import { CopyTextButton } from '../copy-text-button'
import { useModal } from '../modal'

type Props = {
  modal: ReturnType<typeof useModal>
  schoolPersonToEdit: SchoolPersonFragment
}
const SentInvitation = ({ schoolPersonToEdit, modal }: Props) => {
  const theme = useTheme()
  const buttonStyles = useButtonStyles()

  const [state, setState] = useState({
    status: 'summary' as 'summary' | 'resent' | 'canceled',
  })

  const { mutate: resendInvitation } = useHandleReactQueryMutation(
    useSendSchoolPersonInvitationEmailMutation({ onSuccess: () => setState({ status: 'resent' }) })
  )
  const { mutate: deleteInvitation } = useHandleReactQueryMutation(
    useDeleteSchoolPersonInvitationMutation({
      onSuccess: () => setState(_state => ({ ..._state, status: 'canceled' })),
    })
  )

  if (state.status === 'resent') {
    return (
      <div>
        <Typography gutterBottom variant="h6">
          Invitation resent to {schoolPersonToEdit.person?.firstName} at{' '}
          {schoolPersonToEdit.person?.email}.
        </Typography>

        <Typography>
          Tip: it may help to let {schoolPersonToEdit.person?.firstName} know to expect this
          invitation. Consider{' '}
          <a href={`mailto:${schoolPersonToEdit.person?.email}`}>
            sending {schoolPersonToEdit.person?.firstName} an email
          </a>{' '}
          to give them a heads up in case the invitation goes to their junk/spam folder.
        </Typography>

        <Button
          style={{ minWidth: 100, margin: `${theme.spacing(2)}px 0px` }}
          variant="outlined"
          color="primary"
          onClick={modal.close}
        >
          Done
        </Button>
      </div>
    )
  }

  if (state.status === 'canceled') {
    return (
      <div>
        <Typography gutterBottom variant="h6">
          Invitation removed for {schoolPersonToEdit.person?.firstName}.
        </Typography>

        <Button
          style={{ minWidth: 100, margin: `${theme.spacing(2)}px 0px` }}
          variant="outlined"
          color="primary"
          onClick={modal.close}
        >
          Done
        </Button>
      </div>
    )
  }

  if (state.status === 'summary') {
    return (
      <div>
        <Typography gutterBottom variant="h6">
          {schoolPersonToEdit.person?.firstName} was invited{' '}
          {dayjs(schoolPersonToEdit.schoolPersonInvitation?.createdAt).fromNow()} (
          {dayjs(schoolPersonToEdit.schoolPersonInvitation?.createdAt).format('M/D/YY')}).
        </Typography>

        <div style={{ display: 'flex', margin: `${theme.spacing(2)}px 0px` }}>
          <CopyTextButton
            variant="outlined"
            textToCopy={`${process.env.REACT_APP_ROOT_URL}/invitation/${schoolPersonToEdit.schoolPersonInvitation?.slug}`}
            style={{ minWidth: 100, marginRight: theme.spacing(1) }}
          >
            Copy Invitation Link
          </CopyTextButton>

          <Button
            variant="outlined"
            color="primary"
            style={{ minWidth: 100, marginRight: theme.spacing(1) }}
            onClick={() => resendInvitation({ schoolPersonInvitationId: schoolPersonToEdit.id })}
          >
            Resend Invitation
          </Button>
          <Button
            variant="contained"
            className={buttonStyles.error}
            style={{ minWidth: 100, marginRight: theme.spacing(1) }}
            onClick={() => deleteInvitation({ schoolPersonId: schoolPersonToEdit.id })}
          >
            Cancel Invitation
          </Button>
        </div>
      </div>
    )
  }

  return null
}

export default SentInvitation
